import { ClientMessage, ServerMessage } from '@/state/multiplayerTypes';

type Listener<T> = (value: T) => void;

export interface SessionClientOptions {
  sessionId: string;
  playerId?: string;
  playerName?: string;
  onMessage?: Listener<ServerMessage>;
  onClose?: Listener<CloseEvent>;
  onError?: Listener<Event>;
  reconnectIntervalMs?: number;
}

export class SessionClient {
  private readonly sessionId: string;
  private readonly playerName?: string;
  private playerId?: string;
  private socket: WebSocket | null = null;
  private onMessage?: Listener<ServerMessage>;
  private onClose?: Listener<CloseEvent>;
  private onError?: Listener<Event>;
  private reconnectTimer: number | null = null;
  private readonly reconnectIntervalMs: number;
  private readonly pendingMessages: ClientMessage[] = [];
  private shouldReconnect = true;

  constructor(options: SessionClientOptions) {
    this.sessionId = options.sessionId;
    this.playerId = options.playerId;
    this.playerName = options.playerName;
    this.onMessage = options.onMessage;
    this.onClose = options.onClose;
    this.onError = options.onError;
    this.reconnectIntervalMs = options.reconnectIntervalMs ?? 2000;
  }

  connect() {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.shouldReconnect = true;

    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const configuredOrigin = (import.meta.env.VITE_BACKEND_ORIGIN as string | undefined)?.trim();
    const baseUrl = configuredOrigin && configuredOrigin.length > 0
      ? new URL(configuredOrigin)
      : new URL(window.location.href);

    const url = new URL(`/ws/${this.sessionId}`, baseUrl);
    url.search = '';
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      const joinMessage: ClientMessage = {
        type: 'join',
        playerId: this.playerId,
        name: this.playerName
      };
      this.send(joinMessage);
      this.flushPending();
    });

    socket.addEventListener('message', event => {
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        if (message.type === 'init') {
          this.playerId = message.playerId;
        }
        this.onMessage?.(message);
      } catch (error) {
        console.error('Failed to parse server message', error);
      }
    });

    socket.addEventListener('close', event => {
      this.socket = null;
      this.onClose?.(event);
      this.scheduleReconnect();
    });

    socket.addEventListener('error', event => {
      this.onError?.(event);
    });
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.pendingMessages.length = 0;
  }

  send(message: ClientMessage) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.pendingMessages.push(message);
    }
  }

  private flushPending() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    while (this.pendingMessages.length) {
      const message = this.pendingMessages.shift();
      if (message) {
        this.socket.send(JSON.stringify(message));
      }
    }
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect) {
      return;
    }

    if (this.reconnectTimer !== null) {
      return;
    }
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectIntervalMs);
  }
}
